import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useAuthStore } from "@/store/auth";
import { formatDateTime } from "@/utils/formatDate";
import {
  acquireSubscription,
  type SubscriptionHandle,
} from "@/features/chat/api/realtime";
import {
  qk,
  useAddNote,
  useDeleteNote,
  useOccurrenceNotes,
  useToggleGuestNotes,
  useUpdateNote,
} from "../hooks/useMeetings";
import { playNoteChime } from "../sound";
import type { NotePhase, OccurrenceStatus } from "../types";

const PHASES: { value: NotePhase; label: string }[] = [
  { value: "before", label: "Przed" },
  { value: "during", label: "W trakcie" },
  { value: "after", label: "Po" },
];

export function phaseForStatus(status: OccurrenceStatus): NotePhase {
  if (status === "live") return "during";
  if (status === "ended") return "after";
  return "before";
}

interface Props {
  occurrenceId: string;
  occurrenceStatus: OccurrenceStatus;
  isOrganizer: boolean;
  compact?: boolean;
  /** Meeting id + current flag → organizer can unlock notes for guests. */
  meetingId?: string;
  guestNotesEnabled?: boolean;
}

export function MeetingNotesThread({
  occurrenceId,
  occurrenceStatus,
  isOrganizer,
  compact = false,
  meetingId,
  guestNotesEnabled = false,
}: Props) {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const { data: notes = [] } = useOccurrenceNotes(occurrenceId);
  const addNote = useAddNote(occurrenceId);
  const updateNote = useUpdateNote(occurrenceId);
  const deleteNote = useDeleteNote(occurrenceId);
  const toggleGuestNotes = useToggleGuestNotes(meetingId ?? "");

  // Live notes: the host (or any participant) writes and everyone else in the
  // meeting sees it appear. We piggyback on the room channel — the backend
  // publishes `note_changed` on create/edit/delete; we refetch the thread.
  // The subscription is ref-counted (shared with the room), so no extra socket.
  useEffect(() => {
    // The backend refuses `meeting:{id}` for an ended/cancelled occurrence, so
    // asking is a guaranteed 403 — and this thread is opened on past meetings
    // all the time (OccurrenceDetailPage). Nothing is published there any more
    // either: the notes are static history.
    if (occurrenceStatus === "ended" || occurrenceStatus === "cancelled") return;
    let cancelled = false;
    let handle: SubscriptionHandle | null = null;
    let handler: ((ctx: { data: unknown }) => void) | null = null;
    (async () => {
      try {
        const h = await acquireSubscription(`meeting:${occurrenceId}`);
        if (cancelled) {
          h.release();
          return;
        }
        handle = h;
        handler = (ctx) => {
          if ((ctx.data as { type?: string })?.type === "note_changed") {
            void qc.invalidateQueries({ queryKey: qk.notes(occurrenceId) });
            playNoteChime();
          }
        };
        h.sub.on("publication", handler);
      } catch {
        /* channel not subscribable (e.g. ended meeting) — notes stay static */
      }
    })();
    return () => {
      cancelled = true;
      try {
        if (handler) handle?.sub.off("publication", handler);
        handle?.release();
      } catch {
        /* ignore */
      }
    };
  }, [occurrenceId, occurrenceStatus, qc]);

  const [phaseFilter, setPhaseFilter] = useState<NotePhase | "all">("all");
  const [newPhase, setNewPhase] = useState<NotePhase>(
    phaseForStatus(occurrenceStatus),
  );
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const visible =
    phaseFilter === "all" ? notes : notes.filter((n) => n.phase === phaseFilter);

  const submit = async () => {
    const stripped = draft.replace(/<[^>]*>/g, "").trim();
    if (!stripped) return;
    await addNote.mutateAsync({ phase: newPhase, content: draft });
    setDraft("");
  };

  return (
    <div className="flex flex-col gap-3">
      {isOrganizer && meetingId && (
        <label className="flex items-center gap-2 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            checked={guestNotesEnabled}
            disabled={toggleGuestNotes.isPending}
            onChange={(e) => void toggleGuestNotes.mutateAsync(e.target.checked)}
          />
          Pokaż notatki gościom (tylko do odczytu)
        </label>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {(
          [{ value: "all", label: "Wszystkie" }, ...PHASES] as {
            value: NotePhase | "all";
            label: string;
          }[]
        ).map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setPhaseFilter(p.value)}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
              phaseFilter === p.value
                ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className={`space-y-3 ${compact ? "max-h-72 overflow-y-auto pr-1" : ""}`}>
        {visible.length === 0 && (
          <p className="text-sm text-[var(--color-text-muted)]">Brak notatek.</p>
        )}
        {visible.map((note) => {
          const mine = note.author?.id === user?.id;
          const phaseLabel = PHASES.find((p) => p.value === note.phase)?.label;
          return (
            <div
              key={note.id}
              className="rounded-md border border-[var(--color-border)] p-3"
            >
              <div className="mb-1.5 flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
                {note.author && (
                  <UserAvatar
                    userId={note.author.id}
                    fullName={note.author.name}
                    size="xs"
                  />
                )}
                <span className="font-medium text-[var(--color-text-secondary)]">
                  {note.author?.name ?? "—"}
                </span>
                <span>· {formatDateTime(note.created_at)}</span>
                <span className="rounded bg-black/5 px-1.5 py-0.5 dark:bg-white/10">
                  {phaseLabel}
                </span>
                {note.is_edited && <span>(edytowano)</span>}
                <span className="ml-auto flex gap-1">
                  {mine && (
                    <button
                      type="button"
                      title="Edytuj"
                      onClick={() => {
                        setEditingId(note.id);
                        setEditDraft(note.content);
                      }}
                      className="p-0.5 hover:text-[var(--color-text-primary)]"
                    >
                      <Pencil size={13} />
                    </button>
                  )}
                  {(mine || isOrganizer) && (
                    <button
                      type="button"
                      title="Usuń"
                      onClick={() => void deleteNote.mutateAsync(note.id)}
                      className="p-0.5 hover:text-red-500"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </span>
              </div>
              {editingId === note.id ? (
                <div className="space-y-2">
                  <RichTextEditor value={editDraft} onChange={setEditDraft} />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        await updateNote.mutateAsync({
                          noteId: note.id,
                          content: editDraft,
                        });
                        setEditingId(null);
                      }}
                      className="rounded-md bg-[var(--color-primary)] px-3 py-1 text-xs text-white"
                    >
                      Zapisz
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="rounded-md border border-[var(--color-border)] px-3 py-1 text-xs"
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="prose prose-sm max-w-none text-sm text-[var(--color-text-primary)] dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: note.content }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="space-y-2 border-t border-[var(--color-border)] pt-3">
        <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
          Nowa notatka:
          {PHASES.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setNewPhase(p.value)}
              className={`rounded-full border px-2 py-0.5 transition-colors ${
                newPhase === p.value
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                  : "border-[var(--color-border)] hover:border-[var(--color-primary)]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <RichTextEditor
          value={draft}
          onChange={setDraft}
          placeholder="Treść notatki…"
        />
        <div className="flex justify-end">
          <button
            type="button"
            disabled={addNote.isPending}
            onClick={() => void submit()}
            className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            Dodaj notatkę
          </button>
        </div>
      </div>
    </div>
  );
}
